import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import { Spinner, Badge, Button, Card, Row, Col } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import { FiRefreshCw, FiSearch, FiUpload, FiCreditCard, FiDownload, FiX, FiUsers, FiClock, FiCheckCircle, FiAlertCircle, FiCalendar, FiTrendingUp } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import ReportViewDownload from '../Shared/ViewDownloadReports';
import RecentReports from '../Shared/RecentReports';
import UploadModal from '../Shared/UploadModal';
import RecentUpdatesBadge from '../Shared/RecentUpdatesBadge';

const Reports = () => {
    const [reportsData, setReportsData] = useState([]);
    const [productCache, setProductCache] = useState({});
    // const [bankAccountCache, setBankAccountCache] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDownloadMode, setIsDownloadMode] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    // const [loadingBankAccounts, setLoadingBankAccounts] = useState({});
    const [cancelTimer, setCancelTimer] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);

    const fetchingRef = useRef(false);
    const productFetchingRef = useRef({});
    const [timeLeft, setTimeLeft] = useState(90);
    // const bankAccountFetchingRef = useRef({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'none' });

    const [bankAccountsData, setBankAccountsData] = useState([]);

    const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
    const [selectedDateFilter, setSelectedDateFilter] = useState(null);

    const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    const checkAndUpdateOverduePayments = useCallback(async () => {
        try {
            const today = moment().tz("Asia/Singapore");
            const currentMonthYear = today.format("MMM YYYY");
            let updatedCount = 0;

            for (const customer of reportsData) {
                if (!customer.reports || !customer.payment_date) continue;

                const customerDueDate = moment(customer.payment_date).tz("Asia/Singapore");
                const customerDueDay = customerDueDate.date();

                const dueDateThisMonth = moment().tz("Asia/Singapore")
                    .date(customerDueDay)
                    .hour(0).minute(0).second(0).millisecond(0);

                if (today.isSameOrAfter(dueDateThisMonth, 'day')) {
                    const currentMonthReport = customer.reports.find(report =>
                        report.month === currentMonthYear
                    );

                    if (currentMonthReport && currentMonthReport.status === "Pending") {
                        try {
                            await axios.put(
                                `https://www.azzenterprises.com/api/admin/${customer.customer_id}/report-status`,
                                {
                                    month: currentMonthYear,
                                    status: "Unpaid",
                                    comments: `Automatically updated - payment overdue on ${customerDueDay}${getOrdinalSuffix(customerDueDay)}`
                                },
                                { withCredentials: true }
                            );

                            setReportsData(prev => prev.map(cust => {
                                if (cust.customer_id === customer.customer_id) {
                                    const updatedReports = cust.reports.map(report =>
                                        report.month === currentMonthYear
                                            ? {
                                                ...report,
                                                status: "Unpaid",
                                                comments: `Automatically updated - payment overdue on ${customerDueDay}${getOrdinalSuffix(customerDueDay)}`,
                                                updated_time: new Date().toISOString()
                                            }
                                            : report
                                    );
                                    return { ...cust, reports: updatedReports };
                                }
                                return cust;
                            }));

                            updatedCount++;
                        } catch (error) {
                        }
                    }
                }
            }

            if (updatedCount > 0) {
                toast.success(`Updated ${updatedCount} overdue payments`);
            }
        } catch (error) {
        }
    }, [reportsData]);

    useEffect(() => {
        if (reportsData.length > 0 && !loading) {
            checkAndUpdateOverduePayments();
        }
    }, [reportsData, loading, checkAndUpdateOverduePayments]);

    const fetchReports = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        try {
            setLoading(true);
            const res = await axios.get("https://www.azzenterprises.com/api/customers/getreports");
            setReportsData(res.data.allReports || []);
        } catch (err) {
            toast.error("Failed to fetch reports.");
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const fetchAllBankAccountsData = useCallback(async () => {
        try {
            const response = await axios.get(
                "https://www.azzenterprises.com/api/admin/bank-accounts",
                { withCredentials: true }
            );

            setBankAccountsData(response.data || []);
        } catch (error) {
            toast.error("Failed to fetch bank accounts");
            setBankAccountsData([]);
        }
    }, []);

    // const fetchAllBankAccounts = useCallback(async () => {
    //     const rowsWithDDARef = reportsData.filter(row => row.boDDARefNo && !bankAccountCache[row.boDDARefNo] && !bankAccountFetchingRef.current[row.boDDARefNo]);

    //     if (rowsWithDDARef.length === 0) return;

    //     try {
    //         const promises = rowsWithDDARef.map(async (row) => {
    //             if (row.boDDARefNo) {
    //                 return fetchBankAccountNumber(row.boDDARefNo);
    //             }
    //         });

    //         await Promise.all(promises);
    //     } catch (error) {
    //     }
    // }, [reportsData, bankAccountCache, fetchBankAccountNumber]);

    useEffect(() => {
        fetchAllBankAccountsData();
    }, [fetchAllBankAccountsData]);

    const fetchProductData = useCallback(async (productId) => {
        if (!productId || productFetchingRef.current[productId]) {
            return productCache[productId] || null;
        }

        productFetchingRef.current[productId] = true;

        try {
            const res = await axios.get(
                `https://www.azzenterprises.com/api/products/info/${productId}`
            );
            const productData = {
                product_name: res.data.product_name || "—",
                color: res.data.color || "—",
                capacity: res.data.capacity || "—",
                size_and_weight: res.data.size_and_weight || "—",
                display: res.data.display || "—",
                product_description: res.data.product_description || "—",
                price: res.data.price || 0,
                quantity: res.data.quantity || 0,
                status: res.data.status || "—",
                date: res.data.date || "—",
                product_img: res.data.product_img || "—",
                discount_price: res.data.discount_price || 0,
                fullData: true
            };

            setProductCache((prev) => ({ ...prev, [productId]: productData }));
            return productData;
        } catch {
            const errorData = { product_name: "—", fullData: true };
            setProductCache((prev) => ({ ...prev, [productId]: errorData }));
            return errorData;
        } finally {
            productFetchingRef.current[productId] = false;
        }
    }, [productCache]);

    const getProductName = (productId) => {
        if (!productId) return "—";
        const cachedData = productCache[productId];
        if (cachedData) return cachedData.product_name || "—";

        if (!productFetchingRef.current[productId]) {
            fetchProductData(productId);
        }

        return "Loading...";
    };

    const getBankAccountNumber = useCallback((boDDARefNo) => {
        if (!boDDARefNo) return null; // Return null instead of "—"

        const bankAccount = bankAccountsData.find(
            account => account.boDDARefNo === boDDARefNo
        );

        // Return null if no account found or no applicantBankAccNo
        return bankAccount?.applicantBankAccNo || null;
    }, [bankAccountsData]);

    const renderProductTooltip = (productId) => {
        if (!productId) return <Tooltip>No product data</Tooltip>;

        const productData = productCache[productId];

        if (!productData || !productData.fullData) {
            return <Tooltip>Loading product details...</Tooltip>;
        }

        return (
            <Tooltip style={{ maxWidth: "400px" }}>
                <div className="text-start">
                    <strong className="d-block border-bottom pb-1 mb-1">{productData.product_name}</strong>
                    <div className="small">
                        <div><strong>Color:</strong> {productData.color}</div>
                        <div><strong>Capacity:</strong> {productData.capacity}</div>
                        <div><strong>Size & Weight:</strong> {productData.size_and_weight}</div>
                        <div><strong>Display:</strong> {productData.display}</div>
                        <div><strong>Price:</strong> ${productData.price}</div>
                        <div><strong>Quantity:</strong> {productData.quantity}</div>
                        <div><strong>Status:</strong> {productData.status}</div>
                        {productData.product_description && (
                            <div className="mt-1">
                                <strong>Description:</strong>
                                <div className="text-truncate" style={{ maxWidth: "350px" }}>
                                    {productData.product_description}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Tooltip>
        );
    };

    const getMonthReport = (reports, monthOffset = 0) => {
        const month = moment()
            .tz("Asia/Singapore")
            .add(monthOffset, "month")
            .format("MMM YYYY");
        const report = (reports || []).find((r) => r.month === month);
        return { month, status: report ? report.status : null };
    };

    const renderStatusBadge = (reports, monthOffset = 0) => {
        const { month, status } = getMonthReport(reports, monthOffset);
        if (!status) return <span>—</span>;

        const colorCode =
            status === "Paid"
                ? "#16a34a"
                : status === "Unpaid"
                    ? "#dc2626"
                    : status === "Pending"
                        ? "#d97706"
                        : "#6b7280";

        return (
            <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">{month.split(' ')[0]}</span>
                <span
                    style={{
                        backgroundColor: colorCode,
                        color: "#fff",
                        fontSize: "0.6rem",
                        fontWeight: "500",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        display: "inline-block"
                    }}
                >
                    {status}
                </span>
            </div>
        );
    };

    const isPaymentOverdue = (customer) => {
        if (!customer.payment_date) return false;

        const today = moment().tz("Asia/Singapore").startOf('day');
        const customerDueDate = moment(customer.payment_date).tz("Asia/Singapore");
        const customerDueDay = customerDueDate.date();

        const dueDateThisMonth = moment().tz("Asia/Singapore")
            .date(customerDueDay)
            .startOf('day');

        return today.isAfter(dueDateThisMonth, 'day');
    };

    const calculateEMIDetails = useCallback((row) => {
        const billAmount = row.bill_amount || 0;
        const emiPlan = row.emi_plan_duration || 1;
        const reports = row.reports || [];

        const monthlyEMI = billAmount / emiPlan;
        const paidMonths = reports.filter(r => r.status === "Paid").length;
        const paidAmount = paidMonths * monthlyEMI;
        const pendingAmount = billAmount - paidAmount;
        const currentMonthReport = reports.find(r => r.month === currentMonth);

        return {
            monthlyEMI,
            paidAmount,
            pendingAmount,
            paidMonths,
            totalMonths: emiPlan,
            currentMonthStatus: currentMonthReport?.status
        };
    }, [currentMonth]);

    const formatDueDate = useCallback((paymentDate, row) => {
        if (!paymentDate) return {
            day: "—",
            fullDate: "No date",
            isOverdue: false,
            ordinalDay: "—",
            displayDate: "—",
            isNextMonth: false
        };

        const baseDate = moment(paymentDate).tz("Asia/Singapore");
        const { currentMonthStatus } = calculateEMIDetails(row);

        let targetMonthDue;
        let isNextMonth = false;

        if (currentMonthStatus === "Paid") {
            targetMonthDue = moment().tz("Asia/Singapore")
                .add(1, 'month')
                .startOf('month');
            isNextMonth = true;
        } else {
            targetMonthDue = moment().tz("Asia/Singapore")
                .startOf('month');
        }

        // Get the day from the original payment date
        const originalDay = baseDate.date();

        // Get the last day of the target month
        const lastDayOfTargetMonth = targetMonthDue.endOf('month').date();

        // Use the last day of month if original day doesn't exist in target month
        let adjustedDay;
        let wasAdjusted = false;

        if (originalDay > lastDayOfTargetMonth) {
            adjustedDay = lastDayOfTargetMonth;
            wasAdjusted = true;
        } else {
            adjustedDay = originalDay;
        }

        targetMonthDue = targetMonthDue.date(adjustedDay).startOf('day');

        const today = moment().tz("Asia/Singapore").startOf('day');
        const isOverdue = today.isAfter(targetMonthDue, 'day');

        const day = targetMonthDue.date();
        const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
        const month = targetMonthDue.format("MMM");
        const displayDate = `${ordinalDay} ${month}`;

        return {
            day: targetMonthDue.format("DD"),
            fullDate: `${targetMonthDue.format("DD MMM YYYY")}${wasAdjusted ? " (last day)" : ""}`,
            isOverdue: isOverdue,
            ordinalDay: ordinalDay,
            displayDate: displayDate,
            targetMonth: targetMonthDue.format("MMM YYYY"),
            isNextMonth: isNextMonth,
            wasAdjusted: wasAdjusted,
            originalDay: originalDay
        };
    }, [calculateEMIDetails, getOrdinalSuffix]);

    const formatCurrency = (amount) => {
        if (!amount) return "—";
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(amount);
    };

    const formatBankAccountNumber = (accountNumber) => {
        if (!accountNumber || accountNumber === "—") return "—";
        return accountNumber;
    };

    const filteredReports = reportsData.filter(
        (r) => {
            const bankAccountNumber = getBankAccountNumber(r.boDDARefNo);
            return (
                r.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (r.product_id && r.product_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (r.boDDARefNo && r.boDDARefNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (r.applicantBankCode && r.applicantBankCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (bankAccountNumber && bankAccountNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (productCache[r.product_id] &&
                    productCache[r.product_id].product_name &&
                    productCache[r.product_id].product_name.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
    );

    const financialStats = useMemo(() => {
        let totalPendingAmount = 0;
        let totalPaidAmount = 0;
        let todayMonthlyEMI = 0;
        let totalBillAmount = 0;

        filteredReports.forEach(row => {
            const { monthlyEMI, paidAmount, pendingAmount, currentMonthStatus } = calculateEMIDetails(row);

            const billAmount = parseFloat(row.bill_amount) || 0;
            totalBillAmount += billAmount;
            totalPendingAmount += pendingAmount;
            totalPaidAmount += paidAmount;

            if (currentMonthStatus === "Pending" || currentMonthStatus === "Unpaid") {
                todayMonthlyEMI += monthlyEMI;
            }
        });

        return {
            totalPendingAmount,
            totalPaidAmount,
            todayMonthlyEMI,
            totalBillAmount
        };
    }, [filteredReports]);

    const statusCounts = useMemo(() => {
        return {
            Pending: filteredReports.filter((r) =>
                r.reports?.some((rep) => rep.month === currentMonth && rep.status === "Pending")
            ).length,
            Paid: filteredReports.filter((r) =>
                r.reports?.some((rep) => rep.month === currentMonth && rep.status === "Paid")
            ).length,
            Unpaid: filteredReports.filter((r) =>
                r.reports?.some((rep) => rep.month === currentMonth && rep.status === "Unpaid")
            ).length,
        };
    }, [filteredReports, currentMonth]);

    const totalSelectedDeductionAmount = useMemo(() => {
        return selectedRows.reduce((total, row) => {
            const { monthlyEMI, currentMonthStatus } = calculateEMIDetails(row);
            if (currentMonthStatus === "Pending" || currentMonthStatus === "Unpaid") {
                return total + monthlyEMI;
            }
            return total;
        }, 0);
    }, [selectedRows]);

    const selectedStatusCounts = useMemo(() => {
        return {
            Paid: selectedRows.filter(row => {
                const { currentMonthStatus } = calculateEMIDetails(row);
                return currentMonthStatus === "Paid";
            }).length,
            Pending: selectedRows.filter(row => {
                const { currentMonthStatus } = calculateEMIDetails(row);
                return currentMonthStatus === "Pending";
            }).length,
            Unpaid: selectedRows.filter(row => {
                const { currentMonthStatus } = calculateEMIDetails(row);
                return currentMonthStatus === "Unpaid";
            }).length
        };
    }, [selectedRows]);

    const getRecentUpdates = useMemo(() => {
        const today = moment().tz("Asia/Singapore").startOf('day');
        const yesterday = moment().tz("Asia/Singapore").subtract(1, 'day').startOf('day');

        const todayUpdates = [];
        const allUpdates = [];

        reportsData.forEach(customer => {
            if (customer.reports && Array.isArray(customer.reports)) {
                customer.reports.forEach(report => {
                    if (report.updated_time && report.updated_time !== null) {
                        const isSingaporeFormat = /^\d{1,2} \w{3} \d{4}, \d{1,2}:\d{2} [AP]M$/.test(report.updated_time);

                        if (isSingaporeFormat) {
                            try {
                                const updatedTime = moment(report.updated_time, 'DD MMM YYYY, hh:mm A').tz("Asia/Singapore");
                                const isToday = updatedTime.isSame(today, 'day');
                                const isYesterday = updatedTime.isSame(yesterday, 'day');

                                const updateData = {
                                    customerName: customer.full_name,
                                    month: report.month,
                                    status: report.status,
                                    updated_time: report.updated_time,
                                    comments: report.comments,
                                    timestamp: updatedTime,
                                    isToday: isToday,
                                    isYesterday: isYesterday
                                };

                                allUpdates.push(updateData);

                                if (isToday) {
                                    todayUpdates.push(updateData);
                                }
                            } catch (error) {
                                console.error('Error parsing Singapore format date:', report.updated_time);
                            }
                        }
                    }
                });
            }
        });
        return {
            todayUpdates: todayUpdates.sort((a, b) => b.timestamp - a.timestamp),
            allUpdates: allUpdates.sort((a, b) => b.timestamp - a.timestamp)
        };
    }, [reportsData]);



    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            width: "60px",
            sortable: true
        },
        {
            name: "CUSTOMER",
            selector: (row) => row.full_name,
            sortable: true,
            width: "160px",
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: "150px" }} title={row.full_name}>
                    {row.full_name}
                </div>
            )
        },
        {
            name: "PRODUCT",
            cell: (row) => (
                <OverlayTrigger
                    placement="right"
                    overlay={renderProductTooltip(row.product_id)}
                    delay={{ show: 250, hide: 0 }}
                >
                    <span
                        style={{
                            cursor: "pointer",
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "120px"
                        }}
                        className="text-gray-700"
                    >
                        {getProductName(row.product_id)}
                    </span>
                </OverlayTrigger>
            ),
            sortable: false,
            width: "140px",
        },
        {
            name: "DDA REF",
            cell: (row) => (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>DDA Reference Number</Tooltip>}
                >
                    <span className="font-semibold text-gray-600 small">
                        {row.boDDARefNo || "—"}
                    </span>
                </OverlayTrigger>
            ),
            width: "120px",
            sortable: false,
        },
        {
            name: "BANK ACCOUNT",
            cell: (row) => {
                const bankAccount = getBankAccountNumber(row.boDDARefNo);
                if (!bankAccount) return <span className="text-muted small">—</span>;
                const maskedAccount = `•••• ${bankAccount.slice(-4)}`;

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                Full Account: {bankAccount}
                            </Tooltip>
                        }
                    >
                        <div className="d-flex align-items-center gap-1">
                            <FiCreditCard className="text-gray-500" size={12} />
                            <span className="font-semibold text-gray-700 small">
                                {maskedAccount}
                            </span>
                        </div>
                    </OverlayTrigger>
                );
            },
            width: "140px",
            sortable: false,
        },
        {
            name: "BILL AMOUNT",
            selector: (row) => parseFloat(row.bill_amount) || 0,
            cell: (row) => (
                <span className="font-semibold text-gray-800">
                    {formatCurrency(row.bill_amount)}
                </span>
            ),
            width: "110px",
            sortable: true,
        },
        {
            name: "MONTHLY EMI",
            selector: (row) => {
                const { monthlyEMI } = calculateEMIDetails(row);
                return monthlyEMI;
            },
            cell: (row) => {
                const { monthlyEMI, currentMonthStatus } = calculateEMIDetails(row);

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                Monthly EMI: {formatCurrency(monthlyEMI)}
                                <br />
                                Status: {currentMonthStatus || "Not started"}
                            </Tooltip>
                        }
                    >
                        <span style={{ fontWeight: 600 }} className={
                            currentMonthStatus === "Paid" ? "text-success font-semibold" :
                                currentMonthStatus === "Unpaid" ? "text-danger font-semibold" :
                                    "text-warning font-semibold"
                        }>
                            {formatCurrency(monthlyEMI)}
                        </span>
                    </OverlayTrigger>
                );
            },
            width: "120px",
            sortable: true,
        },
        {
            name: "PAID AMOUNT",
            selector: (row) => {
                const { paidAmount } = calculateEMIDetails(row);
                return paidAmount;
            },
            cell: (row) => {
                const { paidAmount } = calculateEMIDetails(row);
                return (
                    <span className="text-green-600 font-semibold">
                        {formatCurrency(paidAmount)}
                    </span>
                );
            },
            width: "120px",
            sortable: true,
        },
        {
            name: "PENDING AMOUNT",
            selector: (row) => {
                const { pendingAmount } = calculateEMIDetails(row);
                return pendingAmount;
            },
            cell: (row) => {
                const { pendingAmount } = calculateEMIDetails(row);
                return (
                    <span className="text-red-600 font-semibold">
                        {formatCurrency(pendingAmount)}
                    </span>
                );
            },
            width: "150px",
            sortable: true,
        },
        {
            name: "Upcoming DUE",
            selector: (row) => {
                const { day } = formatDueDate(row.payment_date, row);
                return parseInt(day) || 0;
            },
            sortable: true,
            cell: (row) => {
                const { displayDate, fullDate, isOverdue, isNextMonth } = formatDueDate(row.payment_date, row);
                const { currentMonthStatus } = calculateEMIDetails(row);

                const showOverdue = isOverdue && currentMonthStatus !== "Paid";

                const getDueDateColor = () => {
                    if (isNextMonth) return "#3b82f6";
                    if (showOverdue) return "#dc2626";
                    if (currentMonthStatus === "Paid") return "#16a34a";
                    if (currentMonthStatus === "Unpaid") return "#dc2626";
                    if (currentMonthStatus === "Pending") return "#d97706";
                    return "#6b7280";
                };

                const dueDateColor = getDueDateColor();
                const textColor = "#fff";

                const getTooltipText = () => {
                    if (isNextMonth) {
                        return `Next EMI due on ${fullDate} (Current month already paid)`;
                    }
                    return showOverdue
                        ? `OVERDUE! Payment was due on ${fullDate}`
                        : `EMI will be auto-deducted on ${fullDate}`;
                };

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{getTooltipText()}</Tooltip>}
                    >
                        <span
                            style={{
                                backgroundColor: dueDateColor,
                                color: textColor,
                                fontSize: "0.7rem",
                                fontWeight: "500",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                display: "inline-block",
                                lineHeight: "1",
                                cursor: "pointer"
                            }}
                        >
                            {displayDate}
                        </span>
                    </OverlayTrigger>
                );
            },
            width: "130px",
        },
        {
            name: "PREV MONTH",
            cell: (row) => renderStatusBadge(row.reports, -1),
            width: "130px"
        },
        {
            name: "CURR MONTH",
            cell: (row) => renderStatusBadge(row.reports, 0),
            width: "130px"
        },
        {
            name: "PROGRESS",
            width: "90px",
            cell: (row) => {
                const total = row.reports?.length || 0;
                const paid = row.reports?.filter((r) => r.status === "Paid").length || 0;
                const percentage = total > 0 ? (paid / total) * 100 : 0;

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{paid} of {total} months paid ({percentage.toFixed(0)}%)</Tooltip>}
                    >
                        <div>
                            <div className="small font-semibold text-center">{paid}/{total}</div>
                            <div className="progress" style={{ height: "4px", width: "60px", backgroundColor: "#e5e7eb" }}>
                                <div
                                    className="progress-bar"
                                    style={{
                                        width: `${percentage}%`,
                                        backgroundColor: percentage === 100 ? "#16a34a" : "#3b82f6"
                                    }}
                                ></div>
                            </div>
                        </div>
                    </OverlayTrigger>
                );
            },
        },
    ];

    const selectableRowSelected = useCallback(
        (row) => isDownloadMode && selectedRows.some((s) => s.full_name === row.full_name),
        [isDownloadMode, selectedRows]
    );

    const isSameSelection = useCallback((current, candidate) => {
        if (current.length !== candidate.length) return false;
        return candidate.every((row) => current.some((s) => s.full_name === row.full_name));
    }, []);

    const toggleStatusSelection = (status) => {
        if (!isDownloadMode) {
            toast.warn("Activate download mode first!");
            return;
        }

        const newSelectedStatuses = selectedStatuses.includes(status)
            ? selectedStatuses.filter(s => s !== status)
            : [...selectedStatuses, status];

        setSelectedStatuses(newSelectedStatuses);
        setSelectedDateFilter(null);

        if (newSelectedStatuses.length === 0) {
            setSelectedRows([]);
            setTimeLeft(90);
            return;
        }

        const matchingRows = filteredReports.filter((r) =>
            r.reports?.some((rep) =>
                rep.month === currentMonth &&
                newSelectedStatuses.includes(rep.status)
            )
        );

        setSelectedRows(matchingRows);
    };

    const getStatusColor = (status) => {
        return status === "Paid"
            ? "#16a34a"
            : status === "Unpaid"
                ? "#dc2626"
                : status === "Pending"
                    ? "#d97706"
                    : "#6b7280";
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "Paid": return <FiCheckCircle size={14} />;
            case "Pending": return <FiClock size={14} />;
            case "Unpaid": return <FiAlertCircle size={14} />;
            default: return <FiUsers size={14} />;
        }
    };

    const handleSelectedRowsChange = useCallback(
        (state) => {
            const newSelected = state.selectedRows;
            if (isSameSelection(selectedRows, newSelected)) return;
            setSelectedRows(newSelected);
            setSelectedDateFilter(null);

            // If no rows selected, reset the countdown timer
            if (!newSelected || newSelected.length === 0) {
                setTimeLeft(90);
            }

            const statusesInSelection = [...new Set(newSelected.map(row => {
                const { currentMonthStatus } = calculateEMIDetails(row);
                return currentMonthStatus;
            }))].filter(Boolean);

            setSelectedStatuses(statusesInSelection);
        },
        [selectedRows, isSameSelection]
    );

    const toggleDownloadMode = () => {
        const newMode = !isDownloadMode;
        setIsDownloadMode(newMode);
        if (cancelTimer) {
            clearTimeout(cancelTimer);
            setCancelTimer(null);
        }

        if (!newMode) {
            setSelectedRows([]);
            setSelectedStatuses([]);
            setSelectedDateFilter(null);
            setTimeLeft(90);
        } else {
            setTimeLeft(90);
            setSelectedDateFilter(null);
        }
    };

    const handleClearOrCancel = () => {
        if (cancelTimer) {
            clearTimeout(cancelTimer);
            setCancelTimer(null);
        }

        setTimeLeft(90);

        if (selectedStatuses.length > 0 || selectedRows.length > 0 || selectedDateFilter) {
            setSelectedStatuses([]);
            setSelectedRows([]);
            setSelectedDateFilter(null);
            toast.info("Selection cleared");
        } else {
            setIsDownloadMode(false);
            setSelectedRows([]);
            setSelectedStatuses([]);
            setSelectedDateFilter(null);
            toast.info("Download mode cancelled");
        }
    };

    useEffect(() => {
        let countdownInterval = null;
        let inactivityTimer = null;

        if (isDownloadMode && selectedRows.length === 0) {
            // Start the visual countdown for the user
            countdownInterval = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            // Start the inactivity timeout (90 seconds)
            inactivityTimer = setTimeout(() => {
                if (selectedRows.length === 0) {
                    setIsDownloadMode(false);
                    setSelectedRows([]);
                    setSelectedStatuses([]);
                    setSelectedDateFilter(null);
                    toast.info("Download mode cancelled due to inactivity");
                }
            }, 90000);

            setCancelTimer(inactivityTimer);

            // Cleanup function
            return () => {
                if (countdownInterval) {
                    clearInterval(countdownInterval);
                }
                if (inactivityTimer) {
                    clearTimeout(inactivityTimer);
                }
            };
        }
    }, [isDownloadMode, selectedRows.length]);

    const StatusFilterButton = ({ status, count, isActive, onClick }) => {
        const activeCount = selectedRows.filter(row => {
            const { currentMonthStatus } = calculateEMIDetails(row);
            return currentMonthStatus === status;
        }).length;

        return (
            <Button
                size="sm"
                variant={isActive ? "dark" : "outline-dark"}
                className="d-flex align-items-center gap-2 border-0"
                style={{
                    fontSize: "0.75rem",
                    fontWeight: "500",
                    backgroundColor: isActive ? getStatusColor(status) : "transparent",
                    color: isActive ? "white" : getStatusColor(status),
                    border: `1px solid ${getStatusColor(status)} !important`,
                    transition: 'all 0.2s ease'
                }}
                onClick={onClick}
            >
                {getStatusIcon(status)}
                {status}
                <Badge
                    bg={isActive ? "light" : "dark"}
                    text={isActive ? "dark" : "light"}
                    style={{
                        fontSize: "0.65rem",
                        backgroundColor: isActive ? "white" : getStatusColor(status),
                        minWidth: '24px'
                    }}
                >
                    {isActive ? `${activeCount}/${count}` : count}
                </Badge>
            </Button>
        );
    };

    const StatCard = ({ title, value, icon, color }) => (
        <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: "48px",
                            height: "48px",
                            backgroundColor: `${color}15`
                        }}
                    >
                        {React.cloneElement(icon, {
                            size: 20,
                            color: color
                        })}
                    </div>
                    <div>
                        <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>{title}</h6>
                        <h4 className="fw-bold mb-0" style={{ color: color, fontSize: "1.5rem" }}>{value}</h4>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );

    const getDueDateCategory = useCallback((row) => {
        if (!row.payment_date) return "no-date";

        const today = moment().tz("Asia/Singapore").startOf('day');
        const customerDueDate = moment(row.payment_date).tz("Asia/Singapore");
        const customerDueDay = customerDueDate.date();

        const { currentMonthStatus } = calculateEMIDetails(row);

        const possibleDueDates = [
            moment().tz("Asia/Singapore").date(customerDueDay).startOf('day'),
            moment().tz("Asia/Singapore").subtract(1, 'month').date(customerDueDay).startOf('day'),
            moment().tz("Asia/Singapore").add(1, 'month').date(customerDueDay).startOf('day')
        ];

        for (const dueDate of possibleDueDates) {
            const diffDays = dueDate.diff(today, 'days');

            if (diffDays === -1) {
                return "yesterday";
            }
        }

        const { isOverdue } = formatDueDate(row.payment_date, row);

        const dueDateThisMonth = moment().tz("Asia/Singapore").date(customerDueDay).startOf('day');
        const actualDueDate = currentMonthStatus === "Paid" ?
            moment().tz("Asia/Singapore").add(1, 'month').date(customerDueDay).startOf('day') :
            dueDateThisMonth;

        const diffDays = actualDueDate.diff(today, 'days');

        if (isOverdue && currentMonthStatus !== "Paid") {
            return "overdue";
        } else if (diffDays === 0) {
            return "today";
        } else if (diffDays === 1) {
            return "tomorrow";
        } else if (diffDays >= 2) {
            return "upcoming";
        }

        return "other";
    }, [calculateEMIDetails, formatDueDate]);

    const dateCategoryCounts = useMemo(() => {
        const counts = {
            yesterday: 0,
            today: 0,
            tomorrow: 0,
            upcoming: 0,
            overdue: 0
        };

        filteredReports.forEach(row => {
            const category = getDueDateCategory(row);
            if (category === "yesterday") counts.yesterday++;
            if (category === "today") counts.today++;
            if (category === "tomorrow") counts.tomorrow++;
            if (category === "upcoming") counts.upcoming++;
            if (category === "overdue") counts.overdue++;
        });

        return counts;
    }, [filteredReports]);

    const DateFilterButton = ({ label, count, isActive, onClick, icon, color, filterKey }) => {
        const activeCount = selectedRows.filter(row => {
            return getDueDateCategory(row) === filterKey;
        }).length;

        return (
            <Button
                size="sm"
                variant={isActive ? "dark" : "outline-dark"}
                className="d-flex align-items-center gap-2 border-0"
                style={{
                    fontSize: "0.75rem",
                    fontWeight: "500",
                    backgroundColor: isActive ? color : "transparent",
                    color: isActive ? "white" : color,
                    border: `1px solid ${color} !important`,
                    transition: 'all 0.2s ease'
                }}
                onClick={onClick}
            >
                {icon}
                {label}
                <Badge
                    bg={isActive ? "light" : "dark"}
                    text={isActive ? "dark" : "light"}
                    style={{
                        fontSize: "0.65rem",
                        backgroundColor: isActive ? "white" : color,
                        minWidth: '24px'
                    }}
                >
                    {isActive ? `${activeCount}/${count}` : count}
                </Badge>
            </Button>
        );
    };

    const getSortedFilteredReports = useMemo(() => {
        if (!selectedDateFilter && selectedStatuses.length === 0) {
            return filteredReports;
        }
        return [...filteredReports].sort((a, b) => {
            const aDateMatch = selectedDateFilter ? getDueDateCategory(a) === selectedDateFilter : false;
            const bDateMatch = selectedDateFilter ? getDueDateCategory(b) === selectedDateFilter : false;

            const { currentMonthStatus: aStatus } = calculateEMIDetails(a);
            const { currentMonthStatus: bStatus } = calculateEMIDetails(b);
            const aStatusMatch = selectedStatuses.length > 0 ? selectedStatuses.includes(aStatus) : false;
            const bStatusMatch = selectedStatuses.length > 0 ? selectedStatuses.includes(bStatus) : false;

            const aMatchesFilter = aDateMatch || aStatusMatch;
            const bMatchesFilter = bDateMatch || bStatusMatch;

            if (aMatchesFilter && !bMatchesFilter) {
                return -1;
            }
            if (!aMatchesFilter && bMatchesFilter) {
                return 1;
            }

            if (aMatchesFilter && bMatchesFilter) {
                if (aDateMatch && !bDateMatch) return -1;
                if (!aDateMatch && bDateMatch) return 1;

                if (aDateMatch && bDateMatch) {
                    const aDue = formatDueDate(a.payment_date, a);
                    const bDue = formatDueDate(b.payment_date, b);
                    return parseInt(aDue.day || 0) - parseInt(bDue.day || 0);
                } else if (aStatusMatch && bStatusMatch) {
                    const statusOrder = { 'Paid': 1, 'Pending': 2, 'Unpaid': 3 };
                    return (statusOrder[aStatus] || 4) - (statusOrder[bStatus] || 4);
                }
            }

            return 0;
        });
    }, [filteredReports, selectedDateFilter, selectedStatuses, getDueDateCategory, calculateEMIDetails, formatDueDate]);

    return (
        <div className="container-fluid py-2">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div>
                                <p className="fw-bold mb-0 d-flex align-items-center">
                                    Monitor customer EMI payments and generate reports
                                </p>
                            </div>
                        </div>
                        <div className="d-flex gap-2">

                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="d-flex align-items-center gap-2 border"
                                onClick={fetchReports}
                                disabled={loading}
                                style={{ fontSize: "12px" }}
                            >
                                <FiRefreshCw size={14} />
                                Refresh
                            </Button>

                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-flex align-items-center gap-2 border"
                                onClick={() => setShowUploadModal(true)}
                                style={{ fontSize: "12px" }}
                            >
                                <FiUpload size={14} />
                                Upload
                            </Button>
                            <Button
                                variant={isDownloadMode ? "dark" : "outline-dark"}
                                size="sm"
                                className="d-flex align-items-center gap-2 border-0"
                                onClick={toggleDownloadMode}
                                style={{
                                    fontSize: "12px",
                                    backgroundColor: isDownloadMode ? "#374151" : "transparent",
                                    color: isDownloadMode ? "white" : "#374151"
                                }}
                            >
                                <FiDownload size={14} />
                                {isDownloadMode ? "Download Mode" : "Download"}
                            </Button>
                        </div>
                    </div>
                </div>
                {!isDownloadMode && <RecentUpdatesBadge getRecentUpdates={getRecentUpdates} />}
            </div>

            {!isDownloadMode && (
                <>
                    <Row className="mb-4 g-3">
                        <Col md={3}>
                            <StatCard
                                title="Total Customers"
                                value={filteredReports.length}
                                icon={<FiUsers />}
                                color="#374151"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Pending"
                                value={statusCounts.Pending}
                                icon={<FiClock />}
                                color="#d97706"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Paid"
                                value={statusCounts.Paid}
                                icon={<FiCheckCircle />}
                                color="#16a34a"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Unpaid"
                                value={statusCounts.Unpaid}
                                icon={<FiAlertCircle />}
                                color="#dc2626"
                            />
                        </Col>
                    </Row>

                    <Row className="mb-4 g-3">
                        <Col md={3}>
                            <StatCard
                                title="Total Pending Amount"
                                value={formatCurrency(financialStats.totalPendingAmount)}
                                icon={<FiClock />}
                                color="#d97706"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Total Paid Amount"
                                value={formatCurrency(financialStats.totalPaidAmount)}
                                icon={<FiCheckCircle />}
                                color="#16a34a"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Today's Monthly EMI"
                                value={formatCurrency(financialStats.todayMonthlyEMI)}
                                icon={<FiCalendar />}
                                color="#3b82f6"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Total Bill Amount"
                                value={formatCurrency(financialStats.totalBillAmount)}
                                icon={<FiTrendingUp />}
                                color="#8b5cf6"
                            />
                        </Col>
                    </Row>
                </>
            )}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-3 border-bottom bg-gray-50">
                        <div className="row g-2 align-items-center">
                            <div className="col-md-12 mb-4">
                                <div className="input-group" style={{ maxWidth: "400px" }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <FiSearch size={16} className="text-gray-500" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search customers, products, DDA references..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: "0.875rem" }}
                                    />
                                </div>
                            </div>

                            {isDownloadMode && (
                                <div className="col-md-12">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">

                                        <div className="d-flex align-items-center gap-2">
                                            <span className="small text-muted fw-medium" style={{ fontSize: "0.7rem" }}>
                                                Due Date:
                                            </span>
                                            <div className="d-flex gap-1">
                                                {[
                                                    { label: "Yesterday", color: "#8b5cf6", key: "yesterday" },
                                                    { label: "Today", color: "#3b82f6", key: "today" },
                                                    { label: "Tomorrow", color: "#10b981", key: "tomorrow" },
                                                ].map(({ label, color, key }) => (
                                                    <DateFilterButton
                                                        key={key}
                                                        label={label}
                                                        count={dateCategoryCounts[key]}
                                                        isActive={selectedDateFilter === key}
                                                        onClick={() => {
                                                            if (selectedDateFilter === key) {
                                                                setSelectedDateFilter(null);
                                                                setSelectedRows([]);
                                                                setTimeLeft(90);
                                                            } else {

                                                                setSelectedDateFilter(key);
                                                                const filteredRows = filteredReports.filter(row => {
                                                                    return getDueDateCategory(row) === key;
                                                                });
                                                                setSelectedRows(filteredRows);
                                                                setSelectedStatuses([]);
                                                            }
                                                        }}
                                                        icon={<FiCalendar size={12} />}
                                                        color={color}
                                                        filterKey={key}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <span className="small text-muted fw-medium" style={{ fontSize: "0.7rem" }}>
                                                Status:
                                            </span>
                                            <div className="d-flex gap-1">
                                                {["Pending", "Paid", "Unpaid"].map((status) => (
                                                    <StatusFilterButton
                                                        key={status}
                                                        status={status}
                                                        count={statusCounts[status]}
                                                        isActive={selectedStatuses.includes(status)}
                                                        onClick={() => toggleStatusSelection(status)}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            {selectedRows.length > 0 && (
                                                <>
                                                    <div className="d-flex align-items-center">
                                                        <Badge
                                                            bg="dark"
                                                            className="px-2 py-1"
                                                            style={{ fontSize: "0.7rem", fontWeight: 600 }}
                                                        >
                                                            {selectedRows.length} selected
                                                        </Badge>
                                                    </div>
                                                </>
                                            )}
                                            <div className="position-relative">
                                                <button
                                                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-2 px-2 py-1 overflow-hidden"
                                                    onClick={handleClearOrCancel}
                                                    style={{
                                                        fontSize: "0.75rem",
                                                        position: "relative",
                                                        minWidth: selectedRows.length > 0 ? "auto" : "100px",
                                                        justifyContent: "space-between",
                                                        transition: "all 0.2s ease"
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#dc3545";
                                                        e.currentTarget.style.color = "white";
                                                        e.currentTarget.style.borderColor = "#dc3545";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = "transparent";
                                                        e.currentTarget.style.color = "#dc3545";
                                                        e.currentTarget.style.borderColor = "#dc3545";
                                                    }}
                                                >

                                                    <div className="d-flex align-items-center gap-1" style={{ zIndex: 1 }}>
                                                        <FiX size={12} />
                                                        <span>{selectedRows.length > 0 ? "Clear" : "Cancel"}</span>
                                                    </div>

                                                    {selectedRows.length === 0 && (
                                                        <div className="position-relative" style={{ zIndex: 1 }}>
                                                            <span className="fw-bold" style={{
                                                                color: timeLeft > 60 ? "#92400e" :
                                                                    timeLeft > 30 ? "#ea580c" : "#dc2626",
                                                                fontSize: "0.7rem",
                                                                transition: "color 0.3s ease"
                                                            }}>
                                                                {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {selectedRows.length === 0 && (
                                                        <div
                                                            className="position-absolute top-0 start-0 h-100"
                                                            style={{
                                                                width: `${((90 - timeLeft) / 90) * 100}%`,
                                                                backgroundColor: timeLeft > 60 ? "rgba(245, 158, 11, 0.08)" :
                                                                    timeLeft > 30 ? "rgba(249, 115, 22, 0.08)" : "rgba(239, 68, 68, 0.08)",
                                                                transition: "width 1s linear, background-color 0.3s ease"
                                                            }}
                                                        />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {isDownloadMode && selectedRows.length > 0 && (
                        <div className="p-3 border-bottom bg-gray-50">
                            <ReportViewDownload
                                selectedRows={selectedRows}
                                selectedStatusCounts={selectedStatusCounts}
                                totalSelectedDeductionAmount={totalSelectedDeductionAmount}
                                formatCurrency={formatCurrency}
                                getProductName={getProductName}
                                getBankAccountNumber={getBankAccountNumber}
                                onClearSelection={handleClearOrCancel}
                                productCache={productCache}
                            // bankAccountCache={bankAccountCache}
                            />
                        </div>
                    )}

                    <div className="p-3">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <Spinner animation="border" variant="secondary" />
                                <span className="ms-2 text-muted">Loading reports...</span>
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={getSortedFilteredReports}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                paginationComponentOptions={{
                                    rowsPerPageText: 'Rows per page:',
                                    rangeSeparatorText: 'of',
                                    selectAllRowsItem: true,
                                    selectAllRowsItemText: 'All',
                                }}
                                selectableRows={isDownloadMode}
                                selectableRowsHighlight
                                onSelectedRowsChange={handleSelectedRowsChange}
                                clearSelectedRows={!isDownloadMode}
                                selectableRowSelected={selectableRowSelected}
                                highlightOnHover
                                responsive
                                striped
                                persistTableHead
                                fixedHeader
                                fixedHeaderScrollHeight="calc(100vh - 300px)"
                                customStyles={{
                                    headCells: {
                                        style: {
                                            backgroundColor: "#f9fafb",
                                            fontWeight: "600",
                                            fontSize: "0.75rem",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            color: "#374151",
                                            borderBottom: "1px solid #e5e7eb"
                                        },
                                    },
                                    cells: {
                                        style: {
                                            fontSize: "0.875rem",
                                            paddingTop: "12px",
                                            paddingBottom: "12px",
                                            color: "#374151"
                                        }
                                    },
                                    rows: {
                                        style: {
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: (row) => {
                                                    const dateMatch = selectedDateFilter ?
                                                        getDueDateCategory(row) === selectedDateFilter : false;
                                                    const { currentMonthStatus } = calculateEMIDetails(row);
                                                    const statusMatch = selectedStatuses.length > 0 ?
                                                        selectedStatuses.includes(currentMonthStatus) : false;

                                                    return (dateMatch || statusMatch) ? '#f0f9ff' : 'inherit';
                                                }
                                            },
                                            '&:nth-of-type(even)': {
                                                backgroundColor: (row) => {
                                                    const dateMatch = selectedDateFilter ?
                                                        getDueDateCategory(row) === selectedDateFilter : false;
                                                    const { currentMonthStatus } = calculateEMIDetails(row);
                                                    const statusMatch = selectedStatuses.length > 0 ?
                                                        selectedStatuses.includes(currentMonthStatus) : false;

                                                    return (dateMatch || statusMatch) ? '#e0f2fe' : 'inherit';
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        )}
                    </div>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-4">
                <Card.Body>
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-3">
                            <Spinner animation="border" size="sm" variant="secondary" />
                            <span className="ms-2 text-muted">Loading recent activity...</span>
                        </div>
                    ) : (
                        <RecentReports />
                    )}
                </Card.Body>
            </Card>

            <ToastContainer
                position="top-right"
                autoClose={2000}
                newestOnTop
                theme="light"
            />
            <UploadModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onUploadSuccess={() => {
                    fetchReports();
                    setShowUploadModal(false);
                }}
            />
        </div>
    );
};

export default Reports;